"use client";

import { useMemo } from "react";
import { ClipboardList, Info, Truck, User } from "lucide-react";
import { DrawerFieldShell } from "@aera/data-table";
import type { ReceivableListItem } from "@/lib/pendingOrders";
import {
  getReceivableOrderId,
  isReceivableAddressItem,
  resolveOwnerUserId,
} from "@/lib/pendingOrders";
import { canEditPendingAddress } from "@/lib/receivableCorrections";
import { postEditPendingAddress } from "@/lib/moneyOperations";
import type { ReceivableDrawerSection } from "./receivableDetailsUtils";

export type PendingAddressDraft = {
  name: string;
  phone: string;
  address: string;
};

const INPUT_CLASS =
  "w-full border-0 bg-transparent p-0 text-sm font-semibold leading-6 text-aera-charcoal shadow-none placeholder:text-aera-lightslate placeholder:font-normal focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:text-aera-lightslate";

const SECTION_ICONS: Record<string, typeof Info> = {
  CUSTOMER: User,
  SHIPPING: Truck,
  DETAILS: ClipboardList,
};

const NAME_KEYS = ["addr_name", "recipient_name", "customer_ref"] as const;
const PHONE_KEYS = [
  "addr_phone",
  "phone",
  "recipient_phone",
  "contact_phone",
] as const;
const ADDRESS_KEYS = ["address_text", "address"] as const;

function fieldValueForInput(value: string): string {
  const trimmed = value.trim();
  return !trimmed || trimmed === "—" ? "" : trimmed;
}

export function pickPendingAddressDraft(
  item: ReceivableListItem,
): PendingAddressDraft {
  const pick = (keys: readonly string[]) => {
    for (const key of keys) {
      const field = item.fields.find((entry) => entry.key === key);
      const value = fieldValueForInput(field?.value ?? "");
      if (value) return value;
    }
    return "";
  };
  return {
    name: pick(NAME_KEYS) || fieldValueForInput(item.title ?? ""),
    phone: pick(PHONE_KEYS) || fieldValueForInput(item.addrPhone ?? ""),
    address: pick(ADDRESS_KEYS) || fieldValueForInput(item.detail ?? ""),
  };
}

function pickFieldMeta(
  fields: ReceivableListItem["fields"],
  keys: readonly string[],
  fallbackLabel: string,
): { key: string; label: string } {
  for (const key of keys) {
    const field = fields.find((entry) => entry.key === key);
    if (field) {
      return { key: field.key, label: field.label || fallbackLabel };
    }
  }
  return { key: keys[0], label: fallbackLabel };
}

export function ReceivablePendingAddressEditor({
  item,
  sections,
  draft,
  onDraftChange,
  disabled = false,
  busy = false,
}: {
  item: ReceivableListItem;
  sections: ReceivableDrawerSection[];
  draft: PendingAddressDraft;
  onDraftChange: (next: PendingAddressDraft) => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  const editable = canEditPendingAddress(item) && isReceivableAddressItem(item);

  const nameMeta = useMemo(
    () =>
      pickFieldMeta(
        sections.flatMap((section) => section.fields),
        NAME_KEYS,
        "ลูกค้า/ผู้รับ",
      ),
    [sections],
  );
  const phoneMeta = useMemo(
    () =>
      pickFieldMeta(
        sections.flatMap((section) => section.fields),
        PHONE_KEYS,
        "โทร",
      ),
    [sections],
  );
  const addressMeta = useMemo(
    () =>
      pickFieldMeta(
        sections.flatMap((section) => section.fields),
        ADDRESS_KEYS,
        "ที่อยู่",
      ),
    [sections],
  );

  if (!editable) return null;

  const locked = disabled || busy;
  const editableSections = sections.filter(
    (section) => section.label === "CUSTOMER" || section.label === "SHIPPING",
  );

  return (
    <>
      {editableSections.map((section) => {
        const SectionIcon = SECTION_ICONS[section.label] ?? Info;
        return (
          <div
            key={section.label}
            className="animate-in fade-in slide-in-from-top-2 duration-500 fill-mode-both"
          >
            <div className="overflow-hidden rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated)">
              <div className="flex items-center gap-2 border-b border-(--aera-dt-border-soft) bg-aera-white px-4 py-3">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-(--aera-dt-row-selected-surface) text-(--aera-dt-accent-hover)">
                  <SectionIcon className="h-4 w-4" />
                </div>
                <h3 className="text-sm font-bold tracking-tight text-aera-charcoal">
                  {section.title}
                </h3>
              </div>
              <div className="divide-y divide-(--aera-dt-border-soft)">
                {section.label === "CUSTOMER" ? (
                  <>
                    <div className="space-y-1.5 px-4 py-3">
                      <label
                        htmlFor="pending-addr-name"
                        className="block text-xs font-semibold leading-5 text-aera-lightslate"
                      >
                        {nameMeta.label}
                      </label>
                      <DrawerFieldShell editable={!locked}>
                        <input
                          id="pending-addr-name"
                          type="text"
                          value={draft.name}
                          disabled={locked}
                          placeholder="ยังไม่ระบุ"
                          onChange={(event) =>
                            onDraftChange({
                              ...draft,
                              name: event.target.value,
                            })
                          }
                          className={INPUT_CLASS}
                        />
                      </DrawerFieldShell>
                    </div>
                    <div className="space-y-1.5 px-4 py-3">
                      <label
                        htmlFor="pending-addr-phone"
                        className="block text-xs font-semibold leading-5 text-aera-lightslate"
                      >
                        {phoneMeta.label}
                      </label>
                      <DrawerFieldShell editable={!locked}>
                        <input
                          id="pending-addr-phone"
                          type="tel"
                          value={draft.phone}
                          disabled={locked}
                          placeholder="ยังไม่ระบุ"
                          onChange={(event) =>
                            onDraftChange({
                              ...draft,
                              phone: event.target.value,
                            })
                          }
                          className={INPUT_CLASS}
                        />
                      </DrawerFieldShell>
                    </div>
                  </>
                ) : (
                  <div className="space-y-1.5 px-4 py-3">
                    <label
                      htmlFor="pending-addr-text"
                      className="block text-xs font-semibold leading-5 text-aera-lightslate"
                    >
                      {addressMeta.label}
                    </label>
                    <DrawerFieldShell multiline editable={!locked}>
                      <textarea
                        id="pending-addr-text"
                        value={draft.address}
                        disabled={locked}
                        rows={3}
                        placeholder="ยังไม่ระบุ"
                        onChange={(event) =>
                          onDraftChange({
                            ...draft,
                            address: event.target.value,
                          })
                        }
                        className={`${INPUT_CLASS} min-h-[72px] resize-none`}
                      />
                    </DrawerFieldShell>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}

export async function savePendingAddressDraft(args: {
  item: ReceivableListItem;
  draft: PendingAddressDraft;
  ownerUserId?: string | null;
  workspaceId?: string | null;
}): Promise<{ summary?: string }> {
  const owner =
    args.ownerUserId?.trim() || resolveOwnerUserId()?.trim() || "";
  const orderId = getReceivableOrderId(args.item);
  const name = args.draft.name.trim();
  const phone = args.draft.phone.trim();
  const address = args.draft.address.trim();

  if (!owner) throw new Error("ไม่พบเจ้าของร้าน");
  if (!orderId) throw new Error("ไม่พบรายการในคิว");
  if (!name || !phone || !address) {
    throw new Error("กรุณาระบุชื่อ เบอร์โทร และที่อยู่ให้ครบ");
  }

  const result = await postEditPendingAddress({
    ownerUserId: owner,
    workspaceId: args.workspaceId ?? undefined,
    orderId,
    addrName: name,
    addrPhone: phone,
    addressText: address,
  });

  return {
    summary: result.summary_lines?.[0],
  };
}

export function isPendingAddressDraftDirty(
  item: ReceivableListItem,
  draft: PendingAddressDraft,
): boolean {
  const original = pickPendingAddressDraft(item);
  return (
    draft.name.trim() !== original.name.trim() ||
    draft.phone.trim() !== original.phone.trim() ||
    draft.address.trim() !== original.address.trim()
  );
}

export function isPendingAddressDraftValid(draft: PendingAddressDraft): boolean {
  return Boolean(
    draft.name.trim() && draft.phone.trim() && draft.address.trim(),
  );
}
